import { cn } from '@/lib/utils'
import {
  Activity,
  AlertTriangle,
  BarChart3,
  BookOpen,
  Briefcase,
  Building2,
  Headphones,
  Home,
  Library,
  ShieldAlert,
  Upload,
  UserRoundCheck,
  Users,
} from 'lucide-react'
import { Link, Outlet, useLocation } from 'react-router-dom'

const adminNavItems = [
  {
    name: 'Dashboard',
    href: '/dashboard',
    icon: Home,
    routeId: 'console.root',
  },
  {
    name: 'Users',
    href: '/users',
    icon: Users,
    routeId: 'console.users',
  },
  {
    name: 'Coach Review',
    href: '/coach-review',
    icon: UserRoundCheck,
    routeId: 'console.root',
  },
  {
    name: 'Job Corpus',
    href: '/job-corpus',
    icon: Briefcase,
    routeId: 'console.root',
  },
  {
    name: 'Crisis Alerts',
    href: '/crisis-alerts',
    icon: ShieldAlert,
    routeId: 'console.root',
  },
  {
    name: 'Organizations',
    href: '/organizations',
    icon: Building2,
    routeId: 'console.root',
  },
  {
    name: 'Import',
    href: '/import',
    icon: Upload,
    routeId: 'console.root',
  },
  {
    name: 'Analytics',
    href: '/analytics',
    icon: BarChart3,
    routeId: 'console.root',
  },
  {
    name: 'Podcasts',
    href: '/podcasts',
    icon: Headphones,
    routeId: 'console.root',
  },
  {
    name: 'Books',
    href: '/books',
    icon: BookOpen,
    routeId: 'console.root',
  },
  {
    name: 'Resources',
    href: '/resources',
    icon: Library,
    routeId: 'console.root',
  },
  {
    name: 'Dead Letter Queues',
    href: '/dlq',
    icon: AlertTriangle,
    routeId: 'console.root',
  },
  {
    name: 'Observability',
    href: '/observability',
    icon: Activity,
    routeId: 'console.root',
  },
]

export function AdminLayout() {
  const location = useLocation()
  const authorization = useAccessControl()
  const visibleNavItems = authorization.data
    ? adminNavItems.filter((item) =>
        canAccessInternalRoute(authorization.data, item.routeId as InternalRouteAccessId),
      )
    : []

  return (
    <div className="flex h-[calc(100vh-4rem)]">
      {/* Sidebar */}
      <aside className="w-64 border-r bg-muted/10 flex flex-col">
        <div className="p-4 border-b">
          <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Admin Console
          </span>
        </div>
        <nav className="flex flex-col gap-1 p-4 flex-1">
          {visibleNavItems.map((item) => {
            const Icon = item.icon
            const isActive =
              location.pathname === item.href ||
              (item.href === '/users' && location.pathname.startsWith('/users'))

            return (
              <Link
                key={item.href}
                to={item.href}
                className={cn(
                  'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-gradient-to-r from-blue-600 to-blue-600 text-white shadow-md'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                )}
              >
                <Icon className="h-5 w-5" />
                {item.name}
              </Link>
            )
          })}
        </nav>
        {/* Footer links */}
        <div className="border-t p-4">
          <div className="flex flex-col gap-2 text-xs text-muted-foreground">
            <div className="flex gap-4">
              <Link to="/terms-of-service" className="hover:text-foreground transition-colors">
                Terms
              </Link>
              <Link to="/privacy" className="hover:text-foreground transition-colors">
                Privacy
              </Link>
            </div>
            <p>&copy; {new Date().getFullYear()} Pelgo</p>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-auto bg-gray-50/50">
        <Outlet />
      </main>
    </div>
  )
}
import { type InternalRouteAccessId, canAccessInternalRoute } from '@/config/route-access'
import { useAccessControl } from '@/hooks/use-access-control'
